import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface Dispositivo {
  id: number; sIdDispositivo: string; sNome: string; iModo: number; ibAtivo: boolean; modoTexto: string;
}
const VAZIO = { Id: 0, SIdDispositivo: "", SNome: "", IModo: 0, IbAtivo: true };

export default function DispositivosPage() {
  const [itens, setItens] = useState<Dispositivo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);
  const [excluirId, setExcluirId] = useState<number | null>(null);

  const carregar = () => {
    setCarregando(true);
    apiClient.get<Dispositivo[]>("/api/rh/ponto/dispositivos")
      .then((r) => setItens(r.data))
      .catch((e) => toast.error(e.message ?? "Falha ao carregar."))
      .finally(() => setCarregando(false));
  };
  useEffect(carregar, []);

  const novo = () => { setForm(VAZIO); setDialog(true); };
  const editar = (d: Dispositivo) => { setForm({ Id: d.id, SIdDispositivo: d.sIdDispositivo, SNome: d.sNome, IModo: d.iModo, IbAtivo: d.ibAtivo }); setDialog(true); };

  const salvar = async () => {
    if (!form.SIdDispositivo.trim()) { toast.warning("Informe o Id do dispositivo."); return; }
    try { await apiClient.post("/api/rh/ponto/dispositivos", form); toast.success("Dispositivo salvo."); setDialog(false); carregar(); }
    catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const excluir = async () => {
    if (excluirId == null) return;
    try { await apiClient.delete(`/api/rh/ponto/dispositivos/${excluirId}`); toast.success("Dispositivo excluído."); setExcluirId(null); carregar(); }
    catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dispositivos de Ponto</h1>
          <p className="text-sm text-muted-foreground">Tablets kiosk e celulares autorizados a registrar ponto.</p>
        </div>
        <Button onClick={novo}><Plus className="h-4 w-4 mr-2" />Novo</Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Nome</TableHead><TableHead>Id do dispositivo</TableHead><TableHead>Modo</TableHead><TableHead className="w-24"></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Nenhum dispositivo.</TableCell></TableRow>
            ) : itens.map((d) => (
              <TableRow key={d.id} className={!d.ibAtivo ? "opacity-50" : undefined}>
                <TableCell>{d.sNome || "—"}{!d.ibAtivo && <Badge variant="outline" className="ml-2">inativo</Badge>}</TableCell>
                <TableCell className="font-mono text-xs">{d.sIdDispositivo}</TableCell>
                <TableCell><Badge variant="secondary">{d.modoTexto}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => editar(d)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setExcluirId(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.Id ? "Editar dispositivo" : "Novo dispositivo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Id do dispositivo</Label>
              <Input value={form.SIdDispositivo} onChange={(e) => setForm({ ...form, SIdDispositivo: e.target.value })} placeholder="ex.: TABLET-RECEPCAO-01" />
            </div>
            <div className="space-y-1"><Label>Nome</Label><Input value={form.SNome} onChange={(e) => setForm({ ...form, SNome: e.target.value })} /></div>
            <div className="space-y-1">
              <Label>Modo</Label>
              <Select value={String(form.IModo)} onValueChange={(v) => setForm({ ...form, IModo: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">Kiosk (compartilhado)</SelectItem><SelectItem value="1">Individual (celular)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} /><Label>Ativo</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluirId != null} onOpenChange={(o) => !o && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir dispositivo?</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={excluir}>Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
